import { useEffect, useRef, type JSX } from 'react';

import type { RunLogLine } from '@/lib/api/quality-types.js';

/** The generator emits its live per-action lines with this marker (see the worker's run_generate.py)
 *  so the feed can present them as plain actions and dim the technical sys/stderr lines. */
const ACTIVITY_PREFIX = '[activity] ';
export const MAX_VISIBLE_LOG_LINES = 200;

/** Which run kind's log a panel shows. Generation and exploratory runs share the same log pipe. */
export type RunLogVariant = 'generation' | 'exploratory';

export function presentLogLine(line: RunLogLine): {
  text: string;
  isActivity: boolean;
} {
  if (line.m.startsWith(ACTIVITY_PREFIX)) {
    return { text: line.m.slice(ACTIVITY_PREFIX.length), isActivity: true };
  }
  return { text: line.m, isActivity: false };
}

/**
 * The scrollable list of generation log lines — activity lines in full colour, technical sys/stderr
 * lines dimmed. Shared by the live feed (while a run is working) and the saved, collapsed log shown
 * under the generated cases. When `live`, auto-scrolls to the newest line as it arrives.
 */
export function GenerationLogLines({
  lines,
  ariaLabel,
  live = false,
}: {
  lines: RunLogLine[];
  ariaLabel: string;
  live?: boolean;
}): JSX.Element {
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Optional-chained call: scrollIntoView is a browser API absent in jsdom (tests) and any
    // non-DOM environment.
    if (live) endRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [live, lines.length]);

  return (
    <div
      className="flex max-h-64 flex-col gap-0.5 overflow-y-auto font-mono text-xs [overflow-wrap:anywhere]"
      role="log"
      aria-live={live ? 'polite' : undefined}
      aria-label={ariaLabel}
    >
      {lines.map((line) => {
        const { text, isActivity } = presentLogLine(line);
        return (
          <div
            key={line.n}
            className={isActivity ? 'text-foreground' : 'text-muted-foreground'}
          >
            {text}
          </div>
        );
      })}
      {live ? <div ref={endRef} /> : null}
    </div>
  );
}
